import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

interface PasswordGateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const PRESALES_PASSWORD = import.meta.env.VITE_PRESALES_PASSWORD || '';

// Warn if password is missing (helps diagnose deployment issues)
if (!PRESALES_PASSWORD && typeof window !== 'undefined') {
  console.warn(
    '⚠️ PasswordGate: VITE_PRESALES_PASSWORD is not set. ' +
    'Password validation will fail. Check that the environment variable is set in your deployment configuration.'
  );
}

export const PasswordGate = ({ open, onOpenChange, onSuccess }: PasswordGateProps) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (import.meta.env.DEV) {
    console.log('PasswordGate render:', { 
      open, 
      hasPassword: !!PRESALES_PASSWORD,
      passwordLength: PRESALES_PASSWORD.length,
      environment: import.meta.env.VITE_ENVIRONMENT || 'unknown'
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Track attempt
    trackEvent('vip_preview_attempt', { success: false });

    // Validate password
    if (!password.trim()) {
      setError('Please enter a password');
      setIsSubmitting(false);
      return;
    }

    // Check if password is configured
    if (!PRESALES_PASSWORD) {
      console.error(
        '❌ PasswordGate: VITE_PRESALES_PASSWORD is not configured. ' +
        'Password validation cannot work. Please set the environment variable in your deployment configuration.'
      );
      setError('Password gate is not properly configured. Please contact support.');
      setIsSubmitting(false);
      return;
    }

    // Check password (case-sensitive)
    if (password === PRESALES_PASSWORD) {
      // Store authentication in localStorage
      localStorage.setItem('pearl_vip_authenticated', 'true');
      
      // Track success
      trackEvent('vip_preview_attempt', { success: true });
      
      // Reset form
      setPassword('');
      setIsSubmitting(false);
      
      // Close dialog and trigger success callback
      onOpenChange(false);
      onSuccess();
    } else {
      setError('Incorrect password. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setPassword('');
      setError('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md overflow-x-hidden">
        <DialogHeader>
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src="/img/logo/thepearl-1.svg"
              alt="The Pearl at Grand Harbour"
              className="h-40 w-auto scale-[2.5]"
            />
          </div>
          <DialogTitle className="text-2xl font-semibold text-center">VIP Preview Access</DialogTitle>
          <DialogDescription className="text-center">
            Enter the presales passcode to access The Pearl at Grand Harbour.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              Passcode
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="Enter passcode"
              className={error ? 'border-destructive' : ''}
              disabled={isSubmitting}
              autoFocus
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !password.trim()}
              className="min-w-[100px]"
            >
              {isSubmitting ? 'Verifying...' : 'Access'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

